import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import type { ProjectContact, SharedProps } from "@/types"

type Props = {
  contact: ProjectContact
  organisations: { id: string; name: string }[]
}

function ContactEdit({ contact, organisations }: Props) {
  const { current_user } = usePage<SharedProps>().props

  const { data, setData, patch, processing } = useForm({
    first_name: contact.first_name || "",
    last_name: contact.last_name || "",
    full_name: contact.full_name || "",
    email: contact.email || "",
    phone_number: contact.phone_number || "",
    role: contact.role || "",
    organisation_id: contact.organisation_id,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/contacts/${contact.id}`)
  }

  return (
    <>
      <Head title={`Modifier ${contact.display_name}`} />
      <PageHeader title={`Modifier ${contact.display_name}`} back={`/contacts/${contact.id}`} />
      <div className="space-y-6">
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Informations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Prénom</Label>
                  <Input
                    id="first_name"
                    value={data.first_name}
                    onChange={(e) => setData("first_name", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Nom</Label>
                  <Input
                    id="last_name"
                    value={data.last_name}
                    onChange={(e) => setData("last_name", e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={data.email}
                    onChange={(e) => setData("email", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone_number">Téléphone</Label>
                  <Input
                    id="phone_number"
                    value={data.phone_number}
                    onChange={(e) => setData("phone_number", e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Rôle / Fonction</Label>
                <Input id="role" value={data.role} onChange={(e) => setData("role", e.target.value)} />
              </div>

              {current_user?.is_super_admin && (
                <div className="space-y-2">
                  <Label htmlFor="organisation">Organisation</Label>
                  <Select value={data.organisation_id} onValueChange={(v) => setData("organisation_id", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une organisation" />
                    </SelectTrigger>
                    <SelectContent>
                      {organisations.map((org) => (
                        <SelectItem key={org.id} value={String(org.id)}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" asChild>
                  <Link href={`/contacts/${contact.id}`}>Annuler</Link>
                </Button>
                <Button type="submit" disabled={processing}>
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </>
  )
}

ContactEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default ContactEdit
